import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useAutocomplete } from '@mui/base/useAutocomplete';
import { styled } from '@mui/material/styles';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import { Checkbox, IconButton, InputBase } from '@mui/material';
import { FixedSizeList } from 'react-window';
import { debounce } from 'lodash';
import { GetGridKeys } from '../dataGridHelper';
import Iconify from '../../iconify';

// keeps the outer popover paper (fixed to 300px max) from ever needing its own
// scrollbar: 300 - paper padding (16) - search box (39) - listbox margin (2) - select all row (36)
const MAX_LIST_HEIGHT = 207;

CustomSelectPanel.propTypes = {
  gridId: PropTypes.string,
  item: PropTypes.object,
  applyValue: PropTypes.func,
  values: PropTypes.array,
  onFilterChange: PropTypes.func,
  appliedFilters: PropTypes.array,
  updateFreeTextFilter: PropTypes.func,
};

export default function CustomSelectPanel({
  gridId,
  item,
  applyValue,
  values,
  appliedFilters,
  onFilterChange,
  updateFreeTextFilter
}) {
  const { baseSearchTextStorageKey } = GetGridKeys(gridId);
  const searchTextStorageKey = `${baseSearchTextStorageKey}-${item.field}`;
  const [searchText, setSearchText] = useState(localStorage.getItem(searchTextStorageKey) || '');

  // captured once on mount (panel remounts each time it's opened) so selected items
  // stay pinned to the top for this session instead of jumping around while checking items
  const [pinnedSelection] = useState(() => new Set(appliedFilters));

  const handleFilterChange = (newValue) => {
    applyValue({ ...item, value: newValue.map((v) => v.value) });
    onFilterChange(newValue);
  };

  useEffect(() => {
    if (appliedFilters.length > values.length) {
      const filtered = values.filter((value) => appliedFilters.includes(value.value));
      if (filtered.length !== appliedFilters.length) {
        handleFilterChange(filtered);
      }
    }
  }, [appliedFilters, values]);

  const myValues = useMemo(
    () => appliedFilters.map((filterValue) => values.find((value) => value.value === filterValue)).filter(Boolean),
    [values, appliedFilters]
  );

  const debouncedHandleInputChange = useMemo(() => debounce((input) => { if (input) updateFreeTextFilter(input); }, 400), []);

  const handleInputChange = (event, newInputValue) => {
    if (event && event.type !== 'blur') {
      setSearchText(newInputValue);
      localStorage.setItem(searchTextStorageKey, newInputValue);
      if (newInputValue === '') handleFilterChange([]);
      debouncedHandleInputChange(newInputValue);
    }
  };

  const {
    getRootProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    focused,
    setAnchorEl,
  } = useAutocomplete({
    multiple: true,
    autoFocus: true,
    id: `${item.field}-autocomplete`,
    options: values,
    onChange: (_, newValue) => handleFilterChange(newValue),
    value: myValues,
    disableCloseOnSelect: true,
    open: true,
    disablePortal: true,
    getOptionLabel: (option) => String(option.value),
    onInputChange: handleInputChange,
    inputValue: searchText,
  });

  // NOTE: sort groupedOptions in place (not a copy) - MUI's useAutocomplete looks up
  // the clicked option via filteredOptions[index], and filteredOptions is the same
  // array reference as groupedOptions, so the displayed order and the click-handling
  // order must stay identical or clicks will toggle the wrong option.
  const opts = groupedOptions.sort((a, b) => {
    const aPinned = pinnedSelection.has(a.value);
    const bPinned = pinnedSelection.has(b.value);
    if (aPinned !== bPinned) return aPinned ? -1 : 1;
    const valueA = typeof a.value === 'string' ? a.value : '';
    const valueB = typeof b.value === 'string' ? b.value : '';
    return valueA.localeCompare(valueB);
  });

  const isOptionSelected = (option) => myValues.some((v) => v.value === option.value);
  const selectAllChecked = opts.length > 0 && opts.every(isOptionSelected);
  const selectAllIndeterminate = !selectAllChecked && opts.some(isOptionSelected);

  const handleSelectAllToggle = () => {
    if (selectAllChecked) {
      handleFilterChange(myValues.filter((v) => !opts.some((o) => o.value === v.value)));
    } else {
      handleFilterChange([...myValues, ...opts.filter((o) => !isOptionSelected(o))]);
    }
  };

  return (
    <Root>
      <div {...getRootProps()}>
        <SearchBar ref={setAnchorEl} className={focused ? 'focused' : ''}>
          <Iconify icon="mdi:magnify" sx={{ color: 'text.disabled', flexShrink: 0 }} />
          <InputBase
            inputProps={{ ...getInputProps() }}
            fullWidth
            placeholder="Search..."
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.stopPropagation();
            }}
            sx={{ fontSize: '14px' }}
          />
          {searchText && (
            <IconButton
              size="small"
              onClick={() => {
                setSearchText('');
                localStorage.setItem(searchTextStorageKey, '');
              }}
              aria-label="Clear search"
              disableRipple
              sx={{ p: 0, flexShrink: 0 }}
            >
              <Iconify icon="mdi:close" sx={{ fontSize: '18px', color: 'text.disabled' }} />
            </IconButton>
          )}
        </SearchBar>
      </div>
      {opts.length > 0 ? (
        <Listbox {...getListboxProps()}>
          <li
            className="select-all-option"
            role="option"
            aria-selected={selectAllChecked}
            tabIndex={-1}
            onClick={handleSelectAllToggle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelectAllToggle();
              }
            }}
          >
            <Checkbox
              size="small"
              disableRipple
              checked={selectAllChecked}
              indeterminate={selectAllIndeterminate}
              sx={{ display: 'flex', alignItems: 'center', p: 0, mr: '10px', pointerEvents: 'none' }}
            />
            <span className="option-label">Select All</span>
          </li>
          <FixedSizeList
            className="filter-options-list"
            height={Math.min(opts.length * 36, MAX_LIST_HEIGHT)}
            width={300}
            itemSize={36}
            itemCount={opts.length}
          >
            {({ index, style }) => {
              const option = opts[index];
              const { key, ...optionProps } = getOptionProps({ option, index });
              return (
                <li key={key} {...optionProps} style={style}>
                  <Checkbox
                    size="small"
                    disableRipple
                    checked={isOptionSelected(option)}
                    sx={{ display: 'flex', alignItems: 'center', p: 0, mr: '10px', pointerEvents: 'none' }}
                  />
                  <span className="option-label">{option.value}</span>
                </li>
              );
            }}
          </FixedSizeList>
        </Listbox>
      ) : null}
    </Root>
  );
}

const Root = styled('div')(
  ({ theme }) => `
  color: ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,.85)'};
  font-size: 14px;


  // MuiPaper-root
  & .MuiPaper-root {
    background-color: ${theme.palette.mode === 'dark' ? '#141414' : '#fff'};
    color: ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,.85)'};

  }
`
);

const SearchBar = styled('div')(
  ({ theme }) => `
  display: flex;
  width: 278px;
  margin: 0 0 0 12px;
  height: 39px;
  padding: 0 10px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  border-radius: 6px;
  border: 1px solid ${theme.palette.mode === 'dark' ? '#434343' : '#C1C9D0'};
  background-color: ${theme.palette.mode === 'dark' ? '#141414' : '#fff'};

  &:hover {
    border-color: ${theme.palette.mode === 'dark' ? '#177ddc' : '#40a9ff'};
  }

  &.focused {
    border-color: ${theme.palette.mode === 'dark' ? '#177ddc' : '#40a9ff'};
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  & input {
    background-color: ${theme.palette.mode === 'dark' ? '#141414' : '#fff'};
    color: ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,.85)'};
    padding: 0;
  }

  & input::placeholder {
    font-size: 12px;
  }

`
);

const Listbox = styled('ul')(
  ({ theme }) => `
  width: 300px;
  margin: 2px 0 0;
  padding: 0;
  list-style: none;
  overflow: hidden;

  & .filter-options-list::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  & .filter-options-list::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 5px;
  }

  & .filter-options-list::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  & li {
    display: flex;
    height: 36px;
    padding: 5px 12px;
    align-items: center;
    align-self: stretch;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.5;
    cursor: pointer;
    box-sizing: border-box;

    & .option-label {
      flex: 1 1 0%;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-right: 8px;
    }
  }

  & li[aria-selected='true'] {
    background-color: ${theme.palette.mode === 'dark' ? '#2b2b2b' : '#fafafa'};
  }

  & li.${autocompleteClasses.focused} {
    background-color: ${theme.palette.mode === 'dark' ? '#003b57' : '#e6f7ff'};
  }
`
);
